import React, { useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Dialog, DialogActions, DialogContent, Grid, Popover } from '@mui/material'
import { useDispatch } from 'react-redux'
import { maxBy } from 'lodash'
import { HISTO, H_24, H_48, J30, J365, J7, J90, OTHER, CIVIL_YEAR, HYDRO_YEAR } from 'alerting/constants/ChartFollowContants'
import { mainBlue, mainWhite } from 'utils/constants/ColorTheme'
import { ButtonMUI } from 'components/styled/Buttons'
import { getLocalStorageJson, onChangeDate, setLocalStorageJson } from 'utils/FormUtils'
import AdministrationAction from 'administration/actions/AdministrationAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import { CHART_SELECTED_TIME, CHART_SELECTED_TIME_OTHER } from 'quality/constants/ChartConstant'
import { getHoverColor } from 'utils/ColorUtil'
import Input from 'components/forms/Input'
import { getDate, momentInRef } from 'utils/DateUtil'
import moment from 'moment'
import { getSubstractTime } from './EChartUtils'
import { DefaultDialogTitle } from 'components/styled/Dialog'

const CHRONIC_TYPE = -1

const TooManyDataDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
}) => (
    <Dialog open={isOpen} slotProps={{ paper: { sx: { minHeight: 'auto', maxHeight: 'auto' } } }}>
        <DefaultDialogTitle title={i18n.payAttention} onClose={onClose} />
        <DialogContent>
            <Grid container>
                <Grid size={12} sx={{ padding: '2.5rem 0 1rem', fontSize: '16px' }}>
                    {i18n.tooManyDataConfirmationMessage}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Grid container size={12} justifyContent='flex-end' alignItems='center'>
                <Grid>
                    <ButtonMUI
                        variant='contained'
                        color='primary'
                        onClick={() => {
                            onValidate()
                            onClose()
                        }}
                    >
                        {i18n.validate}
                    </ButtonMUI>
                </Grid>
            </Grid>
        </DialogActions>
    </Dialog>
)

TooManyDataDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

const MUIChartTabs = ({
    time = J30,
    onChangeTime = () => {},

    statistics = [],
    stationType,
    withSuiviTabs = false,
    timeKey = CHART_SELECTED_TIME,
    otherTimeKey = CHART_SELECTED_TIME_OTHER,
    offsetHours,
}) => {
    const [otherEl, setOtherEl] = useState()
    const storedOtherDates = getLocalStorageJson(otherTimeKey) ?? {}
    const [tempOtherMinDate, setTempOtherMinDate] = useState(time === OTHER ? storedOtherDates.minDate : moment().subtract('91', 'day').valueOf())
    const [tempOtherMaxDate, setTempOtherMaxDate] = useState(time === OTHER ? storedOtherDates.maxDate : moment().valueOf())

    const [isOpenHisto, setIsOpenHisto] = useState(false)

    const getBackgroundColor = (timeScale) => time === timeScale ? mainBlue : mainWhite
    const getColor = (timeScale) => time === timeScale ? mainWhite : mainBlue

    const dispatch = useDispatch()

    const onChange = (newTime) => {
        const minDate = (() => {
            if (newTime === HISTO) {
                return undefined
            }
            if (newTime === CIVIL_YEAR) {
                return moment().startOf('year').valueOf()
            }
            if (newTime === HYDRO_YEAR) {
                return moment().month(9).year(moment().month() >= 9 ? moment().year() : moment().year() - 1).startOf('month').valueOf()
            }
            return getSubstractTime(newTime)
        })()
        const maxDate = (() => {
            if (newTime === HISTO || newTime === CIVIL_YEAR) {
                return moment().endOf('year').valueOf()
            }
            if (newTime === HYDRO_YEAR) {
                return moment().month(9).year(moment().month() >= 9 ? moment().year() : moment().year() - 1).add(1, 'year').month(9).startOf('month').valueOf()
            }
            return undefined
        })()
        onChangeTime({ minDate, maxDate, time: newTime })
        dispatch(AdministrationAction.updateSetting(timeKey, `${newTime}`))
        setLocalStorageJson(timeKey, `${newTime}`)
    }

    const onValidateOtherDates = () => {
        setOtherEl()
        const dates = {
            minDate: tempOtherMinDate && momentInRef(tempOtherMinDate, offsetHours).startOf('day').valueOf(),
            maxDate: tempOtherMaxDate && momentInRef(tempOtherMaxDate, offsetHours).endOf('day').valueOf(),
        }
        onChangeTime({ ...dates, time: OTHER })
        dispatch(AdministrationAction.updateSetting(timeKey, OTHER))
        setLocalStorageJson(timeKey, OTHER)
        setLocalStorageJson(otherTimeKey, dates)
    }

    const isPiezo = stationType === STATION_TYPE_NAME.piezometry

    const checkConfirmation = (toCheck, setIsOpen = () => {}, cb = () => {}) => {
        if (isPiezo && statistics.length) {
            const statBrute = statistics.find(stat => stat.typeId === CHRONIC_TYPE)
            if (toCheck && statBrute && statBrute.countTotal > 10000) {
                setIsOpen(true)
            } else {
                cb()
            }
        } else if ([STATION_TYPE_NAME.hydrometry, STATION_TYPE_NAME.pluviometry].includes(stationType) && statistics.length) {
            if (maxBy(statistics, c => c.countTotal).countTotal > 10000) {
                setIsOpen(true)
            } else {
                cb()
            }
        } else {
            cb()
        }
    }

    return (
        <>
            <Grid
                container
                sx={{ width: withSuiviTabs ? 600 : 480 }}
                alignItems='center'
                justifyContent='space-between'
            >
                <Grid
                    container size={10.25}
                    sx={{
                        boxShadow: '0px 0px 16px 0px rgb(0 0 0 / 6%)',
                        textAlign: 'center',
                        borderRadius: '5px',
                        overflow: 'hidden',
                        backgroundColor: mainWhite,
                        border: `solid 2px ${mainBlue}`,
                        '& div': {
                            lineHeight: '1.1',
                            fontWeight: 600,
                            textAlign: 'center',
                            padding: '0.75rem 0',
                            transition: 'all 0.3s ease-in-out',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        },
                        '& div:not(:last-child)': {
                            borderRight: `solid 2px ${mainBlue}`,
                        },
                    }}
                    alignItems='center'
                    justifyContent='space-between'
                >
                    <Grid
                        size={withSuiviTabs ? 1 : 1.5}
                        className='clickable'
                        onClick={() => onChange(H_24)}
                        sx={{
                            backgroundColor: getBackgroundColor(H_24),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(H_24)),
                            },
                            color: getColor(H_24),
                        }}
                    >
                        {i18n.h24}
                    </Grid>
                    <Grid
                        size={withSuiviTabs ? 1 : 1.5}
                        className='clickable'
                        onClick={() => onChange(H_48)}
                        sx={{
                            backgroundColor: getBackgroundColor(H_48),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(H_48)),
                            },
                            color: getColor(H_48),
                        }}
                    >
                        {i18n.h48}
                    </Grid>
                    <Grid
                        size={withSuiviTabs ? 1 : 1.5}
                        className='clickable'
                        onClick={() => onChange(J7)}
                        sx={{
                            backgroundColor: getBackgroundColor(J7),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(J7)),
                            },
                            color: getColor(J7),
                        }}
                    >
                        {i18n.j7}
                    </Grid>
                    <Grid
                        size={withSuiviTabs ? 1 : 1.5}
                        className='clickable'
                        onClick={() => onChange(J30)}
                        sx={{
                            backgroundColor: getBackgroundColor(J30),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(J30)),
                            },
                            color: getColor(J30),
                        }}
                    >
                        {i18n.j30}
                    </Grid>
                    <Grid
                        size={withSuiviTabs ? 1 : 1.75}
                        className='clickable'
                        onClick={() => onChange(J90)}
                        sx={{
                            backgroundColor: getBackgroundColor(J90),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(J90)),
                            },
                            color: getColor(J90),
                        }}
                    >
                        {i18n.j90}
                    </Grid>
                    <Grid
                        size={withSuiviTabs ? 1 : 1.75}
                        className='clickable'
                        onClick={() => onChange(J365)}
                        sx={{
                            backgroundColor: getBackgroundColor(J365),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(J365)),
                            },
                            color: getColor(J365),
                        }}
                    >
                        {i18n.j365}
                    </Grid>
                    {withSuiviTabs && (
                        <>
                            <Grid
                                size={2.25}
                                className='clickable'
                                onClick={() => onChange(CIVIL_YEAR)}
                                sx={{
                                    backgroundColor: getBackgroundColor(CIVIL_YEAR),
                                    '&:hover': {
                                        backgroundColor: getHoverColor(getBackgroundColor(CIVIL_YEAR)),
                                    },
                                    color: getColor(CIVIL_YEAR),
                                }}
                            >
                                {i18n.civilYear}
                            </Grid>
                            <Grid
                                size={2.25}
                                className='clickable'
                                onClick={() => onChange(HYDRO_YEAR)}
                                sx={{
                                    backgroundColor: getBackgroundColor(HYDRO_YEAR),
                                    '&:hover': {
                                        backgroundColor: getHoverColor(getBackgroundColor(HYDRO_YEAR)),
                                    },
                                    color: getColor(HYDRO_YEAR),
                                }}
                            >
                                {i18n.hydroYear}
                            </Grid>
                        </>
                    )}
                    <Grid
                        size={withSuiviTabs ? 1.5 : 2.5}
                        className='clickable'
                        onClick={() => checkConfirmation(true, setIsOpenHisto, () => onChange(HISTO))}
                        sx={{
                            backgroundColor: getBackgroundColor(HISTO),
                            '&:hover': {
                                backgroundColor: getHoverColor(getBackgroundColor(HISTO)),
                            },
                            color: getColor(HISTO),
                        }}
                    >
                        {i18n.histo}
                    </Grid>
                </Grid>
                <Grid
                    size={1.5}
                    className='clickable'
                    onClick={event => setOtherEl(event.currentTarget)}
                    sx={{
                        boxShadow: '0px 0px 16px 0px rgb(0 0 0 / 6%)',
                        textAlign: 'center',
                        borderRadius: '5px',
                        overflow: 'hidden',
                        border: `solid 2px ${mainBlue}`,
                        lineHeight: '1.1',
                        fontWeight: 600,
                        padding: '0.75rem 0',
                        transition: 'all 0.3s ease-in-out',
                        backgroundColor: getBackgroundColor(OTHER),
                        '&:hover': {
                            backgroundColor: getHoverColor(getBackgroundColor(OTHER)),
                        },
                        color: getColor(OTHER),
                    }}
                >
                    {i18n.other}
                </Grid>
            </Grid>
            <Popover
                open={!!otherEl}
                anchorEl={otherEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{ borderRadius: '8px' }}
                onClose={() => setOtherEl(undefined)}
            >
                <Grid container sx={{ width: '200px', padding: '1rem' }}>
                    <Grid size={12}>
                        <Input
                            title={i18n.startDate}
                            value={getDate(tempOtherMinDate)}
                            onChange={v => onChangeDate(v, v2 => setTempOtherMinDate(v2))}
                            onEnterKeyPress={onValidateOtherDates}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Input
                            title={i18n.endDate}
                            value={getDate(tempOtherMaxDate)}
                            onChange={v => onChangeDate(v, v2 => setTempOtherMaxDate(v2))}
                            onEnterKeyPress={onValidateOtherDates}
                        />
                    </Grid>
                    <Grid size={12} sx={{ paddingTop: '1rem' }}>
                        <ButtonMUI
                            variant='contained'
                            color='primary'
                            fullWidth
                            onClick={onValidateOtherDates}
                        >
                            {i18n.toLoad}
                        </ButtonMUI>
                    </Grid>
                </Grid>
            </Popover>
            <TooManyDataDialog
                isOpen={isOpenHisto}
                onClose={() => setIsOpenHisto(false)}
                onValidate={() => onChange(HISTO)}
            />
        </>
    )
}

MUIChartTabs.propTypes = {
    time: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChangeTime: PropTypes.func,
    statistics: PropTypes.arrayOf(PropTypes.shape({})),
    stationType: PropTypes.string,
    setDisplayCote: PropTypes.func,
    setDiplayModes: PropTypes.func,
    withSuiviTabs: PropTypes.bool,
    timeKey: PropTypes.string,
    otherTimeKey: PropTypes.string,
    offsetHours: PropTypes.number,
}

export default MUIChartTabs